"use client";
import React, { useState, useEffect } from "react";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Input,
  Select,
  SelectItem,
  Chip,
  Pagination,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Spinner,
  Tooltip,
} from "@nextui-org/react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SearchIcon, EyeIcon } from "@/components/icons";

import DefaultLayout from "@/layouts/default";

// Import the API instance to use proper backend URL configuration
import axios from "axios";

// Get API URL from environment variables, same as api.ts
const API_URLS = [
  import.meta.env.VITE_API_URL,
  "http://localhost:8000",
  "http://127.0.0.1:8000",
].filter(Boolean);

const API_URL = API_URLS[0] || "http://localhost:8000";

interface UserLog {
  id: number;
  action: string;
  description: string;
  performed_by: {
    id: number;
    name: string;
    email: string;
  } | null;
  target_user: {
    id: number;
    name: string;
    email: string;
  } | null;
  client: {
    id: number;
    name: string;
  } | null;
  ip_address: string;
  user_agent: string;
  details: any;
  created_at: string;
  formatted_date: string;
  time_ago: string;
}

// Create simple icon components for filter and refresh
const FilterIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
  </svg>
);

const RefreshIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const LogsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [logs, setLogs] = useState<UserLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // User context
  const [currentUserRole] = useState<string>(localStorage.getItem("role") || "");
  const isSuperAdmin = currentUserRole === "superadmin";
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const logsPerPage = 20;
  
  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [showFilters, setShowFilters] = useState(false);
  
  // Available actions for filtering
  const actionTypes = [
    { key: "all", label: "All Actions" },
    { key: "view", label: "View" },
    { key: "edit", label: "Edit" },
    { key: "delete", label: "Delete" },
    { key: "login", label: "Login" },
    { key: "logout", label: "Logout" },
  ];

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let endpoint = "";
      let params = new URLSearchParams();
      
      // Determine which endpoint to use based on user role
      if (isSuperAdmin) {
        endpoint = "recent-logs";
        params.append("limit", "500");
      } else if (currentUserRole === "admin") {
        endpoint = "client-logs";
        params.append("limit", "200");
      } else {
        // Regular users can only see their own logs
        const userId = localStorage.getItem("userId");
        if (!userId) {
          setError("Unable to determine user ID");
          return;
        }
        endpoint = `user-logs/${userId}`;
        params.append("limit", "100");
      }
      
      // Use the proper API URL from environment configuration
      const response = await axios.get(`${API_URL}/api/${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        timeout: 15000,
      });

      const data = response.data;
      
      // Apply client-side filtering
      let filteredLogs = data.logs || [];
      
      if (searchTerm) {
        filteredLogs = filteredLogs.filter((log: UserLog) =>
          log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (log.performed_by?.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (log.target_user?.name?.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }
      
      if (actionFilter !== "all") {
        filteredLogs = filteredLogs.filter((log: UserLog) => log.action === actionFilter);
      }
      
      setTotalLogs(filteredLogs.length);
      
      // Apply pagination
      const startIndex = (currentPage - 1) * logsPerPage;
      const paginatedLogs = filteredLogs.slice(startIndex, startIndex + logsPerPage);
      
      setLogs(paginatedLogs);
      
    } catch (error: any) {
      console.error('Error fetching logs:', error);
      if (error.response?.status === 401) {
        setError("Unauthorized access. Please login again.");
        // Redirect to login page
        navigate("/login");
      } else {
        setError(`Failed to fetch logs: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [currentPage, searchTerm, actionFilter]);

  const getActionColor = (action: string) => {
    switch (action) {
      case 'view':
        return 'primary';
      case 'edit':
        return 'warning';
      case 'delete':
        return 'danger';
      case 'login':
        return 'success';
      case 'logout':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const handleViewUser = (userId: number) => {
    navigate(`/user/${userId}`);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    fetchLogs();
  };

  const totalPages = Math.ceil(totalLogs / logsPerPage);

  if (!["admin", "superadmin"].includes(currentUserRole)) {
    return (
      <DefaultLayout>
        <div className="container mx-auto p-4">
          <div className="bg-red-500 text-white p-4 rounded mb-4">
            {t("You don't have permission to view logs.")}
          </div>
          <Button color="primary" onClick={() => navigate("/dashboard")}>
            {t("Back to Dashboard")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="container mx-auto p-4 max-w-7xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t("Activity Logs")}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              {isSuperAdmin 
                ? t("System-wide activity logs") 
                : t("Activity logs for your organization")
              }
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              color="secondary"
              variant="flat"
              startContent={<FilterIcon className="w-4 h-4" />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {t("Filters")}
            </Button>
            <Button
              color="primary"
              variant="flat"
              startContent={<RefreshIcon className="w-4 h-4" />}
              onClick={handleRefresh}
              isLoading={loading}
            >
              {t("Refresh")}
            </Button>
          </div>
        </div>

        {/* Filters Card */}
        {showFilters && (
          <Card className="mb-6">
            <CardHeader>
              <h3 className="text-lg font-semibold">{t("Filters")}</h3>
            </CardHeader>
            <Divider />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  placeholder={t("Search logs...")}
                  startContent={<SearchIcon className="text-default-400" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  isClearable
                  onClear={() => setSearchTerm("")}
                />
                <Select
                  placeholder={t("Filter by action")}
                  selectedKeys={[actionFilter]}
                  onChange={(e) => setActionFilter(e.target.value)}
                >
                  {actionTypes.map((action) => (
                    <SelectItem key={action.key} value={action.key}>
                      {t(action.label)}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </CardBody>
          </Card>
        )}

        {/* Error Display */}
        {error && (
          <Card className="mb-6 border-red-200 dark:border-red-800">
            <CardBody>
              <div className="text-red-600 dark:text-red-400">{error}</div>
            </CardBody>
          </Card>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardBody className="text-center">
              <p className="text-2xl font-bold text-primary">{totalLogs}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("Total Logs")}
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-2xl font-bold text-success">{totalPages}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("Total Pages")}
              </p>
            </CardBody>
          </Card>
          <Card>
            <CardBody className="text-center">
              <p className="text-2xl font-bold text-warning">{currentPage}</p>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t("Current Page")}
              </p>
            </CardBody>
          </Card>
        </div>

        {/* Logs Table */}
        <Card>
          <CardBody className="p-0">
            {loading ? (
              <div className="flex justify-center items-center p-8">
                <Spinner size="lg" />
                <span className="ml-3">{t("Loading logs...")}</span>
              </div>
            ) : logs.length > 0 ? (
              isSuperAdmin ? (
                // SuperAdmin Table with Client Column
                <Table aria-label="Activity logs table">
                  <TableHeader>
                    <TableColumn>{t("Action")}</TableColumn>
                    <TableColumn>{t("Description")}</TableColumn>
                    <TableColumn>{t("Performed By")}</TableColumn>
                    <TableColumn>{t("Client")}</TableColumn>
                    <TableColumn>{t("Target/Object")}</TableColumn>
                    <TableColumn>{t("Date")}</TableColumn>
                    <TableColumn>{t("IP Address")}</TableColumn>
                    <TableColumn>{t("Actions")}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Chip
                            color={getActionColor(log.action)}
                            size="sm"
                            variant="flat"
                          >
                            {log.action}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate" title={log.description}>
                            {log.description}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.performed_by ? (
                            <div>
                              <p className="font-medium">{log.performed_by.name}</p>
                              <p className="text-xs text-gray-500">{log.performed_by.email}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">{t("System")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.client ? (
                            <span className="text-sm">{log.client.name}</span>
                          ) : (
                            <span className="text-gray-400">{t("N/A")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.target_user ? (
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium text-sm">{log.target_user.name}</p>
                                <p className="text-xs text-gray-500">{log.target_user.email}</p>
                              </div>
                              <Tooltip content={t("View User")}>
                                <Button
                                  isIconOnly
                                  size="sm"
                                  variant="light"
                                  onClick={() => handleViewUser(log.target_user!.id)}
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </Button>
                              </Tooltip>
                            </div>
                          ) : log.details?.book_name ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.book_name}</p>
                              <p className="text-xs text-gray-500">{log.details.author || 'Book'}</p>
                            </div>
                          ) : log.details?.buyer_name ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.buyer_name}</p>
                              <p className="text-xs text-gray-500">{log.details.invoice_number || 'Sale'}</p>
                            </div>
                          ) : log.details?.quantity_added || log.details?.quantity ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.book_name || 'Inventory'}</p>
                              <p className="text-xs text-gray-500">Qty: {log.details.quantity_added || log.details.quantity}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">{t("N/A")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{log.formatted_date}</p>
                            <p className="text-xs text-gray-500">{log.time_ago}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-mono">
                            {log.ip_address || t("Unknown")}
                          </span>
                        </TableCell>
                        <TableCell>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <Tooltip content={t("View Details")}>
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                onClick={() => {
                                  alert(JSON.stringify(log.details, null, 2));
                                }}
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                // Admin Table without Client Column
                <Table aria-label="Activity logs table">
                  <TableHeader>
                    <TableColumn>{t("Action")}</TableColumn>
                    <TableColumn>{t("Description")}</TableColumn>
                    <TableColumn>{t("Performed By")}</TableColumn>
                    <TableColumn>{t("Target/Object")}</TableColumn>
                    <TableColumn>{t("Date")}</TableColumn>
                    <TableColumn>{t("IP Address")}</TableColumn>
                    <TableColumn>{t("Actions")}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Chip
                            color={getActionColor(log.action)}
                            size="sm"
                            variant="flat"
                          >
                            {log.action}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-xs truncate" title={log.description}>
                            {log.description}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.performed_by ? (
                            <div>
                              <p className="font-medium">{log.performed_by.name}</p>
                              <p className="text-xs text-gray-500">{log.performed_by.email}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">{t("System")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.target_user ? (
                            <div className="flex items-center gap-2">
                              <div>
                                <p className="font-medium text-sm">{log.target_user.name}</p>
                                <p className="text-xs text-gray-500">{log.target_user.email}</p>
                              </div>
                              <Tooltip content={t("View User")}>
                                <Button
                                  isIconOnly
                                  size="sm"
                                  variant="light"
                                  onClick={() => handleViewUser(log.target_user!.id)}
                                >
                                  <EyeIcon className="w-4 h-4" />
                                </Button>
                              </Tooltip>
                            </div>
                          ) : log.details?.book_name ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.book_name}</p>
                              <p className="text-xs text-gray-500">{log.details.author || 'Book'}</p>
                            </div>
                          ) : log.details?.buyer_name ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.buyer_name}</p>
                              <p className="text-xs text-gray-500">{log.details.invoice_number || 'Sale'}</p>
                            </div>
                          ) : log.details?.quantity_added || log.details?.quantity ? (
                            <div>
                              <p className="font-medium text-sm">{log.details.book_name || 'Inventory'}</p>
                              <p className="text-xs text-gray-500">Qty: {log.details.quantity_added || log.details.quantity}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">{t("N/A")}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm">{log.formatted_date}</p>
                            <p className="text-xs text-gray-500">{log.time_ago}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-mono">
                            {log.ip_address || t("Unknown")}
                          </span>
                        </TableCell>
                        <TableCell>
                          {log.details && Object.keys(log.details).length > 0 && (
                            <Tooltip content={t("View Details")}>
                              <Button
                                isIconOnly
                                size="sm"
                                variant="light"
                                onClick={() => {
                                  alert(JSON.stringify(log.details, null, 2));
                                }}
                              >
                                <EyeIcon className="w-4 h-4" />
                              </Button>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>{t("No logs found")}</p>
                {(searchTerm || actionFilter !== "all") && (
                  <Button
                    variant="light"
                    onClick={() => {
                      setSearchTerm("");
                      setActionFilter("all");
                    }}
                    className="mt-2"
                  >
                    {t("Clear Filters")}
                  </Button>
                )}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center mt-6">
            <Pagination
              total={totalPages}
              initialPage={1}
              page={currentPage}
              onChange={setCurrentPage}
              showControls
              showShadow
            />
          </div>
        )}
      </div>
    </DefaultLayout>
  );
};

export default LogsPage; 